import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class LocalService {
    constructor() {
    }

    private x_PI = 3.14159265358979324 * 3000.0 / 180.0;
    private PI = 3.1415926535897932384626;
    private a = 6378245.0;
    private ee = 0.00669342162296594323;

    /**
     * 百度坐标系 (BD-09) 与火星坐标系 (GCJ-02) 的转换
     * 即百度 转 谷歌、高德
     * @param lng 经度
     * @param lat 纬度
     * @returns [lng, lat]
     */
    bd09togcj02(lng: number, lat: number): number[] {
        var x_pi = 3.14159265358979324 * 3000.0 / 180.0;
        var x = lng - 0.0065;
        var y = lat - 0.006;
        var z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * x_pi);
        var theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * x_pi);
        var gg_lng = z * Math.cos(theta);
        var gg_lat = z * Math.sin(theta);
        return [gg_lng, gg_lat]
    }

    /**
     * 火星坐标系 (GCJ-02) 与百度坐标系 (BD-09) 的转换
     * 即谷歌、高德 转 百度
     * @param lng 经度
     * @param lat 纬度
     * @returns [lng, lat]
     */
    gcj02tobd09(lng: number, lat: number): number[] {
        var z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * this.x_PI);
        var theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * this.x_PI);
        var bd_lng = z * Math.cos(theta) + 0.0065;
        var bd_lat = z * Math.sin(theta) + 0.006;
        return [bd_lng, bd_lat]
    }

    /**
     * WGS84转火星坐标系 (GCJ-02)
     * 即WGS84 转 谷歌、高德
     * @param lng 经度
     * @param lat 纬度
     * @returns [lng, lat]
     */
    wgs84togcj02(lng: number, lat: number): number[] {
        if (this.out_of_china(lng, lat)) {
            return [lng, lat]
        }
        else {
            var dlat = this.transformlat(lng - 105.0, lat - 35.0);
            var dlng = this.transformlng(lng - 105.0, lat - 35.0);
            var radlat = lat / 180.0 * this.PI;
            var magic = Math.sin(radlat);
            magic = 1 - this.ee * magic * magic;
            var sqrtmagic = Math.sqrt(magic);
            dlat = (dlat * 180.0) / ((this.a * (1 - this.ee)) / (magic * sqrtmagic) * this.PI);
            dlng = (dlng * 180.0) / (this.a / sqrtmagic * Math.cos(radlat) * this.PI);
            var mglat = lat + dlat;
            var mglng = lng + dlng;
            return [mglng, mglat]
        }
    }

    /**
     * 火星坐标系 (GCJ-02)转WGS84
     * 即谷歌、高德 转 WGS84
     * @param lng 经度
     * @param lat 纬度
     * @returns [lng, lat]
     */
    gcj02towgs84(lng: number, lat: number): number[] {
        if (this.out_of_china(lng, lat)) {
            return [lng, lat]
        }
        else {
            var dlat = this.transformlat(lng - 105.0, lat - 35.0);
            var dlng = this.transformlng(lng - 105.0, lat - 35.0);
            var radlat = lat / 180.0 * this.PI;
            var magic = Math.sin(radlat);
            magic = 1 - this.ee * magic * magic;
            var sqrtmagic = Math.sqrt(magic);
            dlat = (dlat * 180.0) / ((this.a * (1 - this.ee)) / (magic * sqrtmagic) * this.PI);
            dlng = (dlng * 180.0) / (this.a / sqrtmagic * Math.cos(radlat) * this.PI);
            let mglat = lat + dlat;
            let mglng = lng + dlng;
            return [lng * 2 - mglng, lat * 2 - mglat]
        }
    }

    private transformlat(lng: number, lat: number) {
        var ret = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng));
        ret += (20.0 * Math.sin(6.0 * lng * this.PI) + 20.0 * Math.sin(2.0 * lng * this.PI)) * 2.0 / 3.0;
        ret += (20.0 * Math.sin(lat * this.PI) + 40.0 * Math.sin(lat / 3.0 * this.PI)) * 2.0 / 3.0;
        ret += (160.0 * Math.sin(lat / 12.0 * this.PI) + 320 * Math.sin(lat * this.PI / 30.0)) * 2.0 / 3.0;
        return ret
    }

    private transformlng(lng: number, lat: number) {
        var ret = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng));
        ret += (20.0 * Math.sin(6.0 * lng * this.PI) + 20.0 * Math.sin(2.0 * lng * this.PI)) * 2.0 / 3.0;
        ret += (20.0 * Math.sin(lng * this.PI) + 40.0 * Math.sin(lng / 3.0 * this.PI)) * 2.0 / 3.0;
        ret += (150.0 * Math.sin(lng / 12.0 * this.PI) + 300.0 * Math.sin(lng / 30.0 * this.PI)) * 2.0 / 3.0;
        return ret
    }

    /**
     * 判断是否在国内，不在国内则不做偏移
     */
    private out_of_china(lng: number, lat: number) {
        return (lng < 72.004 || lng > 137.8347) || ((lat < 0.8293 || lat > 55.8271) || false);
    }

    /**
     * jsonp封装
     * @param obj \{url: '', success: ()=> {}}
     */
    jsonpFunction(obj: any) {
        // 创建一个script标签;
        var script = document.createElement("script");
        // 给函数起名字，定义为全局函数；
        var fnName: any = "jsonp" + new Date().getTime();
        // window.aaa就是把aaa设置为全局变量！
        window[fnName] = obj.success;
        // 改变src,添加到head中
        if (obj.url.includes('?')) {
            script.src = obj.url + "&callback=" + fnName;
        } else {
            script.src = obj.url + "?callback=" + fnName;
        }
        // 把script标签添加到head标签中，就会发送src的请求了
        document.head.appendChild(script);
        // 代码执行完毕，把script标签删除
        script.onload = function () {
            document.head.removeChild(script);
            (window[fnName] as any) = undefined;
        }
    }
}

